import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const { user, login, loginWithGoogle, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [registerMode, setRegisterMode] = useState(false);
  const [name, setName] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user === null) {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          window.google.accounts.id.initialize({
            client_id: '552973092045-0p4o56pigs2770mjtot5bmf93nkc2uvt.apps.googleusercontent.com',
            callback: handleGoogleResponse,
          });

          window.google.accounts.id.renderButton(
            document.getElementById('google-login-button'),
            { theme: 'outline', size: 'large', width: '100%' }
          );

          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleGoogleResponse = async (response: any) => {
    setError(null);
    try {
      const success = await loginWithGoogle(response.credential);
      if (success) {
        navigate("/");
      } else {
        setError("Erreur de connexion avec Google.");
      }
    } catch (err) {
      console.error("Google login error", err);
      setError("Erreur de communication avec le serveur.");
    }
  };

  const handleClassicLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (registerMode) {
      const res = await fetch("/backend/api/register.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password, name }),
      });

      const data = await res.json();
      if (data.success) {
        navigate("/");
      } else {
        setError(data.error || "Erreur lors de la création du compte.");
      }

    } else if (forgotMode) {
      const res = await fetch("/backend/api/forgot-password.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (data.success) {
        setMessage("Un lien de réinitialisation a été envoyé par email.");
        setError(null);
      } else {
        setError(data.error || "Erreur lors de la demande.");
      }

    } else {
      const success = await login(email, password);
      if (success) {
        navigate("/");
      } else {
        setError("Email ou mot de passe incorrect.");
      }
    }
  };

  // ✅ Si déjà connecté, on affiche uniquement un message et le bouton de déconnexion
  if (user) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Bienvenue, {user.email}</h2>
          <button style={styles.button} onClick={() => logout()}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.title}>
          {registerMode ? "Créer un compte" : forgotMode ? "Mot de passe oublié" : "Connexion"}
        </h2>

        {error && <div style={styles.error}>{error}</div>}
        {message && <div style={styles.success}>{message}</div>}

        <form onSubmit={handleClassicLogin} style={styles.form}>
          {registerMode && (
            <input
              type="text"
              placeholder="Nom"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={styles.input}
            />
          )}

          <input
            type="email"
            placeholder="Adresse e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={styles.input}
          />

          {!forgotMode && (
            <input
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={styles.input}
            />
          )}

          <button type="submit" style={styles.button}>
            {registerMode ? "S'inscrire" : forgotMode ? "Envoyer le lien" : "Se connecter"}
          </button>
        </form>

        {!forgotMode && (
          <div style={{ marginTop: "1rem" }}>
            <p style={{ marginBottom: "0.5rem", color: "#888" }}>ou</p>
            <div id="google-login-button"></div>
          </div>
        )}

        <div style={{ marginTop: "1.5rem", fontSize: "0.9rem", color: "#555" }}>
          {!registerMode && !forgotMode && (
            <>
              <p>
                Pas encore de compte ?{" "}
                <span style={styles.link} onClick={() => setRegisterMode(true)}>
                  S'inscrire
                </span>
              </p>
              <p>
                <span style={styles.link} onClick={() => setForgotMode(true)}>
                  Mot de passe oublié ?
                </span>
              </p>
            </>
          )}

          {(registerMode || forgotMode) && (
            <p>
              <span style={styles.link} onClick={() => {
                setRegisterMode(false);
                setForgotMode(false);
                setError(null);
                setMessage(null);
              }}>
                Retour à la connexion
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    background: "#f7f9fc",
  },
  card: {
    background: "#fff",
    padding: "2rem",
    borderRadius: "1rem",
    boxShadow: "0 0 20px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: 400,
    textAlign: "center",
  },
  title: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    fontWeight: 600,
    color: "#333",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  input: {
    padding: "0.75rem 1rem",
    borderRadius: "0.5rem",
    border: "1px solid #ccc",
    fontSize: "1rem",
  },
  button: {
    padding: "0.75rem 1rem",
    background: "#007BFF",
    color: "#fff",
    border: "none",
    borderRadius: "0.5rem",
    fontSize: "1rem",
    cursor: "pointer",
  },
  error: {
    background: "#ffe0e0",
    color: "#900",
    padding: "0.5rem",
    borderRadius: "0.5rem",
    marginBottom: "1rem",
  },
  success: {
    background: "#e0ffe0",
    color: "#090",
    padding: "0.5rem",
    borderRadius: "0.5rem",
    marginBottom: "1rem",
  },
  link: {
    color: "#007BFF",
    cursor: "pointer",
    textDecoration: "underline",
  },
};
